// The second column of the core (um/2026-10-30/04 п.1, DESIGN.md «Вторая колонка»): the list inside a section — letters, articles,
// pages of a site, forms, taxonomies, people — beside the reading column. One width for all sections (--list-w, the person drags it,
// remembered for all of them), one tone, one head (name and «+» in one line; the search is the global one,
// R-U23), one card of a row, one empty state. On a phone one column
// at a time: the list, or what is open with «К списку» in its top line.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ColResizer, LIST_W } from './ColWidth.tsx';
import { Checkbox } from './controls.tsx';
import { Icon } from './icons.tsx';
import { cx, titleIfCut, useNarrow } from './primitives.tsx';
import { TopBack, TopBar, TopTitle, type TopAction } from './TopBar.tsx';
import s from './list-reader.module.css';

export function ListReader({ list, reader, reading, onBack, selection, listLabel, readerLabel }: {
  list: ReactNode; reader: ReactNode; listLabel: string; readerLabel: string;
  /** Something is open in the reading column (the mail: a letter): on a phone it takes the screen; `onBack` closes it. */
  reading?: boolean; onBack?: () => void;
  /**
   * Without `reading`: the chosen row (a form, a page). The first row chosen by itself keeps a phone on the list; a click on a row or
   * another choice opens the reading column.
   */
  selection?: string | null;
}) {
  const narrow = useNarrow();
  const [step, setStep] = useState<'list' | 'reader'>('list');
  const prev = useRef(selection);
  useEffect(() => {
    const before = prev.current;
    prev.current = selection;
    if (before && selection && before !== selection) setStep('reader');
  }, [selection]);
  const controlled = reading !== undefined;
  const open = controlled ? reading : step === 'reader';
  const back = !narrow || !open ? null : controlled ? onBack ?? null : () => setStep('list');
  return (
    <div className={s.screen} data-reading={open || undefined}>
      <section className={s.list} aria-label={listLabel}
        onClick={controlled ? undefined : (e) => { if ((e.target as HTMLElement).closest('[data-list-row]')) setStep('reader'); }}>
        {list}
        {!narrow && <ColResizer width={LIST_W} label="Ширина списка" />}
      </section>
      <section className={s.reader} aria-label={readerLabel}>
        <TopBack.Provider value={back}>{reader}</TopBack.Provider>
      </section>
    </div>
  );
}

/**
 * The head of the list — the core's top line in the list column: its name (the page's H1, or H2 under a page that has its own) and
 * «+» at the right edge as the main action of every section; a choice of kinds (a new form) opens from «+». No search field: the list
 * gives what it holds to the global search (`useScreenSearch`, R-U23), which looks on the open screen first.
 */
export function ListHead({ title, note, add, level = 1, children }: {
  title: ReactNode; note?: ReactNode;
  add?: { label: string; icon?: string; onClick?: () => void; menu?: TopAction['menu'] };
  /** 2 — the list stands under a page with its own name (Настройки → Команда). */
  level?: 1 | 2;
  /** Anything else of the head, before «+». */
  children?: ReactNode;
}) {
  return (
    <TopBar className={s.head} start={<TopTitle as={level === 1 ? 'h1' : 'h2'} note={note}>{title}</TopTitle>} end={children}
      actions={add ? [{ key: 'add', label: add.label, icon: add.icon ?? 'plus', primary: true, onClick: add.onClick ?? (() => {}), menu: add.menu }] : []} />
  );
}

/** A line under the head (the mail's selection, a notice): the same paddings as the head. */
export function ListBar({ children }: { children: ReactNode }) {
  return <div className={s.bar}>{children}</div>;
}

/** The scrolled rows. */
export function ListBody({ children, label, multi, bodyRef }: { children: ReactNode; label: string; multi?: boolean; bodyRef?: React.Ref<HTMLDivElement> }) {
  return <div ref={bodyRef} className={s.body} role="listbox" aria-label={label} aria-multiselectable={multi || undefined}>{children}</div>;
}

/**
 * The card of a row, one for every list of the second column (R-U21: articles, pages, forms, letters): the mark at the left, the name
 * with a note at the right, up to two lines under it. The mark column is always there, so names stand on one line in every list:
 * a status dot, the dot of something new, the box that chooses rows (shown under the pointer and once something is chosen), or a
 * face. Every line is one line; what does not fit ends with «…», and the pointer shows the whole name.
 */
export function ListRow({ selected, onClick, dot, dotLabel, check, lead, title, aside, sub, sub2, strong, className, ...rest }: {
  selected?: boolean; onClick?: (e: React.MouseEvent) => void; title: ReactNode; aside?: ReactNode; sub?: ReactNode; sub2?: ReactNode;
  /** The status dot: green — on (published, accepting answers, switched on), grey — off, accent — new (an unread letter). */
  dot?: 'on' | 'off' | 'new'; dotLabel?: string;
  /** The box of a list whose rows are chosen together (the mail): it stands in the place of the dot under the pointer; a chosen row is tinted. */
  check?: { checked: boolean; label: string; onChange: () => void };
  /** A face instead of the mark (a person of the team). */
  lead?: ReactNode;
  /** Bold name (an unread letter). */
  strong?: boolean; className?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'onClick'>) {
  return (
    <div role="option" aria-selected={!!selected} tabIndex={0} data-list-row className={cx(s.row, selected && s.rowOn, strong && s.strong, check?.checked && s.rowChecked, className)} onClick={onClick}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); e.currentTarget.click(); } }} {...rest}>
      {lead !== undefined ? <span className={s.face}>{lead}</span> : (
        <span className={cx(s.mark, check && s.markCheck)}>
          {dot && <span className={cx(s.dot, dot === 'on' && s.dotOn, dot === 'new' && s.dotNew)} role={dotLabel ? 'img' : undefined} aria-label={dotLabel} />}
          {check && <Checkbox className={s.box} label={check.label} checked={check.checked} onClick={(e) => e.stopPropagation()} onChange={() => check.onChange()} />}
        </span>
      )}
      <span className={s.rowBody}>
        <span className={s.line}><span className={s.name} onMouseEnter={titleIfCut}>{title}</span>{aside && <span className={s.aside}>{aside}</span>}</span>
        {sub && <span className={cx(s.line, s.sub)}>{text(sub)}</span>}
        {sub2 && <span className={cx(s.line, s.sub)}>{text(sub2)}</span>}
      </span>
    </div>
  );
}

/** A line given as text is cut with «…»; a line of parts (the subject with marks) lays them out itself. */
const text = (x: ReactNode) => (typeof x === 'string' || typeof x === 'number' ? <span className={s.text} onMouseEnter={titleIfCut}>{x}</span> : x);

/** A status dot of a row: green — on (published, accepting answers, switched on). */
export function StatusDot({ on, label }: { on: boolean; label?: string }) {
  return <span className={cx(s.dot, on && s.dotOn)} aria-label={label} />;
}

/** The empty list or the empty reading column: an icon, what is missing and what to do. */
export function ListEmpty({ icon, text, hint, action }: { icon?: string; text: ReactNode; hint?: ReactNode; action?: ReactNode }) {
  return (
    <div className={s.empty}>
      {icon && <Icon name={icon} size={28} />}
      <span>{text}</span>
      {hint && <span className={s.hint}>{hint}</span>}
      {action}
    </div>
  );
}

export const listReaderCss = s;
